import { Alert, Box, Button, Divider, Stack } from "@mui/material";
import { useIsModificationReasonSatisfied } from "@src/appV2/ExperimentalHomeHealth/components/ModificationReasonField";
import SvgCheck from "@src/appV2/redesign/assets/illustrations/Check";
import { Text } from "@src/appV2/redesign/components/Text";
import { getFloatingNavigationScrollPaddingBottom } from "@src/appV2/redesign/components/FloatingNavigation/scrollPadding";
import { Title } from "@src/appV2/redesign/components/Title";
import { useQueryClient } from "@tanstack/react-query";
import { addHours } from "date-fns";
import { useState } from "react";
import { useHistory, useParams } from "react-router-dom";

import { getCaseQueryKey, useCase } from "../api/useCase";
import { useCreateVisit } from "../api/useCreateVisit";
import { useFormatTimestampForWorkplace } from "../api/useFormatTimestampForWorkplace";
import { useUpdateCase } from "../api/useUpdateCase";
import { useCanSetRates } from "../../lib/useCanSetRates";
import { useCurrentUserId } from "../../lib/useCurrentUserId";
import { getHomeHealthCaseMobileDetailPath } from "../paths";
import { PricingType, VisitStatus, VisitType, WorkerRequirements } from "../types";
import { MobileAddVisitStep1, type AddVisitStep1Data } from "./MobileAddVisitStep1";
import { MobileAddVisitStep2, type AddVisitStep2Data } from "./MobileAddVisitStep2";
import { MobileAddVisitStep3, type AddVisitStep3Data } from "./MobileAddVisitStep3";

interface WizardFormData extends AddVisitStep1Data, AddVisitStep2Data, AddVisitStep3Data {}

const DEFAULT_FORM_DATA: WizardFormData = {
  visitType: "",
  workerRequirement: "",
  pricingType: PricingType.PER_VISIT,
  specialties: [],
  description: "",
  visitsPerWeek: "",
  totalWeeks: "",
  firstVisitDueDate: "",
  chargePerVisit: "",
  payPerVisit: "",
  modificationReason: "",
};

const TOTAL_STEPS = 3;

const buttonBaseSx = {
  flex: 1,
  borderRadius: "24px",
  py: 1.5,
  textTransform: "none",
  fontSize: "1rem",
} as const;

export function MobileAddVisitWizardContainer() {
  const { caseId } = useParams<{ caseId: string }>();
  const history = useHistory();
  const workplaceId = useCurrentUserId() ?? "";
  const queryClient = useQueryClient();

  const { data: caseItem } = useCase(caseId);
  const patientAddress = caseItem?.attributes.patient.attributes.formattedAddress ?? "";

  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState<WizardFormData>(DEFAULT_FORM_DATA);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);

  const { mutateAsync: createVisit, isLoading: isSubmitting } = useCreateVisit();
  const { mutateAsync: updateCase } = useUpdateCase(caseId);

  const isModificationReasonSatisfied = useIsModificationReasonSatisfied(
    formData.modificationReason
  );
  const canSetRates = useCanSetRates();
  const formatTimestampForWorkplace = useFormatTimestampForWorkplace();

  function handleChange(field: keyof WizardFormData, value: string | string[] | PricingType) {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }

  // Block "Next"/"Submit" until the fields visible on the current step are
  // filled in. Schedule fields only show for regular visits, and rate fields
  // only show when the user can set rates, so they are only required then.
  function isStepValid(currentStep: number): boolean {
    if (currentStep === 0) {
      return Boolean(formData.visitType) && Boolean(formData.workerRequirement);
    }
    if (currentStep === 1) {
      const isRegularVisit = formData.visitType === VisitType.REGULAR;
      const hasSchedule = isRegularVisit
        ? Boolean(formData.visitsPerWeek) && Boolean(formData.totalWeeks)
        : true;
      // Match the submit guard, which only sends a rate when it parses to a
      // finite number, so a non-numeric entry can't pass the step gate and then
      // get silently dropped.
      const hasRates = canSetRates
        ? Number.isFinite(Number.parseFloat(formData.chargePerVisit)) &&
          Number.isFinite(Number.parseFloat(formData.payPerVisit))
        : true;
      return (
        Boolean(formData.firstVisitDueDate) &&
        hasSchedule &&
        hasRates &&
        isModificationReasonSatisfied
      );
    }
    return true;
  }

  function parseDateFieldToISO(dateString: string): string {
    const [yearStr, monthStr, dayStr] = dateString.split("-");
    const localDate = new Date(Number(yearStr), Number(monthStr) - 1, Number(dayStr));
    return formatTimestampForWorkplace(addHours(localDate, 12));
  }

  async function handleSubmit() {
    setSubmitError(null);
    try {
      await updateCase({
        data: {
          attributes: {
            specialties: formData.specialties,
            description: formData.description,
            modificationReason: formData.modificationReason,
          },
          type: "case",
        },
      });

      await createVisit({
        data: {
          attributes: {
            status: VisitStatus.OPEN,
            caseId,
            type: formData.visitType as VisitType,
            workerReq: (formData.workerRequirement as WorkerRequirements) || undefined,
            pricingType: formData.pricingType,
            interestedWorkersIds: [],
            invitedWorkersIds: [],
            invitedWorkersNames: [],
            modificationReason: formData.modificationReason,
            ...(formData.visitType === VisitType.REGULAR && formData.visitsPerWeek
              ? { visitsPerWeek: Number.parseInt(formData.visitsPerWeek, 10) }
              : {}),
            ...(formData.visitType === VisitType.REGULAR && formData.totalWeeks
              ? { durationInWeeks: Number.parseInt(formData.totalWeeks, 10) }
              : {}),
            ...(formData.firstVisitDueDate
              ? { deadline: parseDateFieldToISO(formData.firstVisitDueDate) }
              : {}),
            ...(formData.chargePerVisit &&
            Number.isFinite(Number.parseFloat(formData.chargePerVisit))
              ? {
                  chargeRate: {
                    amountInMinorUnits: Math.round(
                      Number.parseFloat(formData.chargePerVisit) * 100
                    ),
                    currencyCode: "USD" as const,
                  },
                }
              : {}),
            ...(formData.payPerVisit && Number.isFinite(Number.parseFloat(formData.payPerVisit))
              ? {
                  payRate: {
                    amountInMinorUnits: Math.round(Number.parseFloat(formData.payPerVisit) * 100),
                    currencyCode: "USD" as const,
                  },
                }
              : {}),
          },
          type: "visit",
        },
      });

      await queryClient.invalidateQueries(getCaseQueryKey({ workplaceId, caseId }));
      setIsSuccess(true);
    } catch {
      setSubmitError("Something went wrong. Please try again.");
    }
  }

  const paddingX = 3;

  return (
    <Stack
      sx={(theme) => ({
        // Fill (not exceed) the fixed-height, overflow-hidden page wrapper so the
        // header and footer stay pinned and only the middle section scrolls.
        // minHeight:"100vh" let tall content grow the column past the clipped
        // parent, pushing the footer (Submit) out of view on small screens.
        height: "100%",
        minHeight: 0,
        bgcolor: "background.baseDark",
        // Clear the floating Menu pill and the iOS home bar so the footer
        // buttons aren't overlapped or jammed against the bottom of the screen.
        paddingBottom: getFloatingNavigationScrollPaddingBottom(theme) ?? "4rem",
      })}
    >
      <Stack sx={{ px: paddingX, pt: 4, gap: 3 }}>
        <Title component="h5">Add a visit</Title>
        <Divider />
        {!isSuccess && (
          <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
            {(["step-1", "step-2", "step-3"] as const).map((key, i) => (
              <Box
                key={key}
                sx={{
                  flex: 1,
                  height: 8,
                  borderRadius: 3,
                  bgcolor: i <= step ? "base.secondary" : "transparent",
                  border: "1px solid",
                  borderColor: "base.secondary",
                }}
              />
            ))}
          </Stack>
        )}
      </Stack>

      <Stack sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: paddingX, py: 3 }}>
        {submitError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {submitError}
          </Alert>
        )}
        {isSuccess ? (
          <Stack alignItems="center" justifyContent="center" spacing={3} sx={{ flex: 1, px: 3 }}>
            <SvgCheck />
            <Title component="h5" sx={{ fontWeight: "bold" }}>
              Visit posted
            </Title>
            <Text variant="body1" sx={{ textAlign: "center" }}>
              The visit is now open and visible to local professionals!
            </Text>
          </Stack>
        ) : step === 0 ? (
          <MobileAddVisitStep1 data={formData} onChange={handleChange} />
        ) : step === 1 ? (
          <MobileAddVisitStep3
            data={formData}
            pricingType={formData.pricingType}
            visitType={(formData.visitType as VisitType) || undefined}
            workerRequirement={(formData.workerRequirement as WorkerRequirements) || undefined}
            patientAddress={patientAddress}
            onChange={handleChange}
          />
        ) : (
          <MobileAddVisitStep2 data={formData} onChange={handleChange} />
        )}
      </Stack>

      <Stack direction="row" spacing={2} sx={{ px: paddingX, pb: 4, pt: 2 }}>
        {isSuccess ? (
          <Button
            fullWidth
            variant="primary"
            onClick={() => {
              history.replace(getHomeHealthCaseMobileDetailPath(caseId));
            }}
            sx={{ ...buttonBaseSx }}
          >
            OK
          </Button>
        ) : (
          <>
            <Button
              variant="secondary"
              disabled={isSubmitting}
              onClick={() => {
                if (step === 0) {
                  history.goBack();
                } else {
                  setStep((s) => s - 1);
                }
              }}
              sx={{ ...buttonBaseSx }}
            >
              Back
            </Button>
            <Button
              variant="primary"
              disabled={isSubmitting || !isStepValid(step)}
              onClick={() => {
                if (step < TOTAL_STEPS - 1) {
                  setStep((s) => s + 1);
                } else {
                  void handleSubmit();
                }
              }}
              sx={{ ...buttonBaseSx }}
            >
              {step < TOTAL_STEPS - 1 ? "Next" : "Submit"}
            </Button>
          </>
        )}
      </Stack>
    </Stack>
  );
}
